import { createSlice } from '@reduxjs/toolkit';
import { customThemeOptions } from 'src/theme/customTheme';
import { type RootState } from '../store';
import { setOpenDrawer, toggleMobileDrawer } from './actions/drawerActions';
import { setCustomTheme } from './actions/setThemeType';
import LocalCache from 'src/common/LocalCache';

/** Type for custom theme options in settings state. */
export type TCustomTheme = { primary: string; secondary: string; mode: 'light' | 'dark' };

/** Type for settings state in Redux store. */
export type TSettingsState = {
  customTheme: TCustomTheme;
  openMobileDrawer: boolean;
};

/** Initial state for settings slice. */
const initialState: TSettingsState = {
  customTheme: {
    primary: (customThemeOptions.palette?.primary as any).main,
    secondary: (customThemeOptions.palette?.secondary as any).main,
    mode: LocalCache.getTheme() || 'light',
  },
  openMobileDrawer: false,
};

/** Redux slice for application settings state and reducers. */
export const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    // Use the PayloadAction type to declare the contents of `action.payload`
    customThemeAction: setCustomTheme,
    openDrawerAction: setOpenDrawer,
    toggleMobileDrawerAction: toggleMobileDrawer,
  },
});

/** Action to set the custom theme. */
export const { customThemeAction, openDrawerAction, toggleMobileDrawerAction } = settingsSlice.actions;

/** Selector to get the custom theme from settings state. */
export const selectCustomTheme = (state: RootState) => state.settings.customTheme;
/** Selector to get the mobile drawer open state from settings state. */
export const selectMobileDrawer = (state: RootState) => state.settings.openMobileDrawer;
